import {
  HomeIcon,
  ArrowTrendingUpIcon,
  BookmarkIcon,
  UserIcon,
  SunIcon,
  MoonIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";

import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../../assets/images/Logo.webp";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../slices/1-auth/1-login/reducer";
import { MinusIcon } from "lucide-react";
import LanguageDropdown from "../../sharedComponents/LanguageDropdown";
import avatar from "../../../assets/images/Avatar.avif"

const LeftSidebar = ({ onClick }) => {
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [dark, setDark] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openTab, setOpenTab] = useState(null);
  const { profile } = useSelector((state) => state.myProfile);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");
      setDark(true);
    }
  }, []);



  const menu = [
    {
      name: t("home"),
      icon: HomeIcon,
      path: "/",
      roles: ["admin", "user", "owner"],
    },
    {
      name: t("trending.Tab"),
      icon: ArrowTrendingUpIcon,
      path: "/trending",
      roles: ["admin", "user", "owner"],
    },
    {
      name: t("saved"),
      icon: BookmarkIcon,
      path: "/saved",
      roles: ["admin", "user", "owner"],
    },
    {
      name: t("profile.tab"),
      icon: UserIcon,
      path: "/profile",
      roles: ["admin", "user", "owner"],
    },
    {
      name: t("company.tab"),
      icon: BuildingOfficeIcon,
      path: "mycompany",
      roles: ["owner"],
    },
    {
      name: t("settings.Tab"),
      icon: Cog6ToothIcon,
      path: "/settings/users",
      roles: ["admin"],
      subTabs: [
        {
          name: t("settings.Users"),
          path: "/settings/users",
          roles: ["admin"],
        },
        {
          name: t("settings.Companies"),
          path: "/settings/companies",
          roles: ["admin"],
        },
        {
          name: t("settings.Reviews"),
          path: "/settings/reviews",
          roles: ["admin"],
        },
        {
          name: t("settings.Posts"),
          path: "/settings/posts",
          roles: ["admin"],
        },
        {
          name: t("settings.contact"),
          path: "/settings/contact",
          roles: ["admin"],
        },
      ],
    },
  ];

  const role = profile?.role || "user";
  const filteredMenu = menu.filter(item =>
    !item.roles || item.roles.includes(role)
  );

  const toggleTheme = () => {
    if (dark) {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    } else {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    }
    setDark(!dark);
  };



  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);

    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  };
  return (
    <aside
      className="fixed start-0 top-0 lg:w-[16rem] w-full h-screen
             bg-bg-light-secondary dark:bg-bg-dark-secondary
             border-r rtl:border-l border-border-light dark:border-border-dark
             flex flex-col justify-between p-4 transition-colors overflow-y-auto scrollbar-hide
"
    >
      {/* Logo */}
      <div>
        <div className="flex items-center gap-3 mb-6 cursor-pointer"
          onClick={() => {
            navigate("/");
            onClick?.()
          }}
        >
          <img
            className="w-12 h-12 border-[3px] rounded-full dark:border-primary border-primary-light"
            alt="logo"
            src={logo}
          />
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t("website.name")}
            <p className="text-xs text-text-muted-light dark:text-text-muted-dark">{t("website.subtitle")}</p>
          </h1>
        </div>

        {/* Menu */}
        <nav className="flex flex-col pb-4 space-y-2 border-b border-border-light dark:border-border-dark">
          {filteredMenu.map((item, i) => {
            const hasSubTabs = item.subTabs?.length > 0;
            const isOpen = openTab === item.name;
            const isParentActive =
              location.pathname === item.path ||
              item.subTabs?.some(sub => location.pathname.startsWith(sub.path));

            return (
              <div key={i}>
                {/* MAIN TAB */}
                <NavLink
                  to={item.path}
                  onClick={(e) => {
                    if (hasSubTabs) {
                      e.preventDefault();
                      setOpenTab(isOpen ? null : item.name);
                    }
                    else {
                      onClick?.()
                    }
                  }}
                >
                  {({ isActive }) => {
                    const active = isActive || isParentActive;

                    return (
                      <div
                        className={`relative group
        flex items-center justify-between px-5 py-3 rounded-xl cursor-pointer
        transition-colors duration-300 hover:text-black dark:hover:text-white hover:bg-[#E2E9E6]
        ${active
                            ? "bg-[#E2E9E6] dark:bg-white/5 text-black dark:text-white"
                            : "text-text-muted-light dark:text-text-muted-dark hover:bg-[#E2E9E6] dark:hover:bg-white/5"}
      `}
                      >
                        {/* LEFT SIDE (icon + name) */}
                        <div className="flex items-center gap-4">
                          {/* Curved Left Border */}
                          {active && (
                            <span className="absolute inset-0 border-green-500 pointer-events-none rounded-xl ltr:border-l-4 rtl:border-r-4" />
                          )}

                          <item.icon className={`"w-6 h-6 ${active ? "dark:text-primary text-primary-light" : "text-text-muted-light dark:text-text-muted-dark dark:group-hover:text-primary group-hover:text-primary-light duration-300"}`} />
                          <span className="">{item.name}</span>
                        </div>

                        {/* RIGHT SIDE (arrow) */}
                        {hasSubTabs && (
                          <ChevronDownIcon
                            className={`
            w-5 h-5 transition-transform duration-300
            ${isOpen ? "rotate-180 dark:text-primary text-primary-light" : "rotate-0"}
          `}
                          />
                        )}
                      </div>
                    )
                  }
                  }
                </NavLink>


                {/* SUB TABS */}
                {hasSubTabs && (
                  <div
                    className={`
      ms-2 mt-1 space-y-1 overflow-hidden
      transition-all duration-300
      ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}
    `}
                  >
                    {item.subTabs
                      .filter(sub => !sub.roles || sub.roles.includes(role))
                      .map((sub, idx) => (
                        <NavLink key={idx} to={sub.path}
                          onClick={() => onClick?.()}>
                          {({ isActive }) => (
                            <div
                              className={`
                                py-2 rounded-lg text-base ms-4 flex items-center gap-4
                                ${isActive
                                  ? " dark:text-primary text-primary-light"
                                  : "dark:text-text-muted-dark text-text-muted-light duration-300 hover:text-black dark:hover:text-white"}
                              `}
                            >
                              <span className="w-5 h-6 "><MinusIcon className="w-4 h-5 " />
                              </span>  {" "} {sub.name}
                            </div>
                          )}
                        </NavLink>
                      ))}
                  </div>
                )}

              </div>
            );
          })}
        </nav>

        <div className="px-5 py-3 space-y-4">
          <h1 className="text-text-muted-light">{t("theme")}</h1>
          <div className="flex items-center gap-4">
            {/* Sun */}
            <SunIcon
              className={`w-5 h-5 ${dark ? "text-primary" : "text-primary-light"}`}
            />

            {/* Switch */}
            <button
              onClick={toggleTheme}
              className={`relative w-14 h-7 rounded-full transition-colors duration-300
          ${dark ? "bg-primary" : "bg-bg-light-tertiary"}`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 rounded-full ${dark ? "bg-bg-dark-tertiary" : "bg-primary-light"} transition-transform duration-300
            ${dark ? "translate-x-7" : "translate-x-0"}`}
              />
            </button>

            {/* Moon */}
            <MoonIcon
              className={`w-5 h-5 ${dark ? "text-primary" : "text-primary-light"}`}
            />
          </div>
          <h1 className="text-text-muted-light">{t("settings.language")}</h1>
          <LanguageDropdown i18n={i18n} changeLanguage={changeLanguage} />
        </div>
      </div>

      {/* User */}
      <div>
        <div className="flex items-center gap-3 pt-4 mt-4 border-t border-border-light dark:border-border-dark">
          <div className="relative cursor-pointer"
            onClick={() => navigate("/profile")}
          >
            <img
              src={profile?.avatar || avatar}
              alt="avatar"
              className="w-10 h-10 border-2 rounded-full border-border-light dark:border-border-dark"
            />

            {/* Online dot */}
            <span
              className="absolute bottom-0 right-0 w-3 h-3 rounded-full dark:bg-primary bg-primary-light shadow-glow"
            />
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-900 cursor-pointer dark:text-gray-100"
              onClick={() => {
                navigate("/profile")
                onClick?.()
              }}
            >
              {profile?.fullName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">@{profile?.code}</p>
          </div>
        </div>

        {/* Sign in / Sign out */}
        <div
          onClick={() => {
            dispatch(logout());
            navigate("/login");
          }} className="flex items-center gap-3 px-2 py-2 mt-3 transition-colors duration-300 rounded-lg cursor-pointer text-text-muted-light dark:text-text-muted-dark hover:text-red-500 dark:hover:bg-bg-dark-tertiary hover:bg-bg-light-tertiary"
        >

          <ArrowRightOnRectangleIcon className="w-5 h-5" />

          <span className="text-sm font-medium"
          >
            {t("sign_out")}
          </span>
        </div>
      </div>
    </aside>
  );
};

export default LeftSidebar;
